/* eslint-disable react/prop-types, react/jsx-handler-names */

import React, { useState, useMemo } from 'react';
import Select from 'react-select';
import { useTheme } from '@mui/material/styles';
import NoSsr from '@mui/material/NoSsr';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import CancelIcon from '@mui/icons-material/Cancel';
import { Person, BlockRounded } from '@mui/icons-material';
import Avatar from '@mui/material/Avatar';
import { buildInlineSelectStyles } from '#component/form/react-select-styles';
import propTypes from './prop-types';
import {
  ErrorContainer,
  InputContainer,
  MessageTypography,
  PlaceholderTypography,
  RootContainer,
  SingleValueTypography,
  StyledChip,
  ValueContainerDiv
} from './styles';

function NoOptionsMessage(props) {
  return (
    <MessageTypography color="textSecondary" {...props.innerProps}>
      {props.children}
    </MessageTypography>
  );
}

const inputComponent = React.forwardRef(
  (props, ref) => <InputContainer ref={ref} {...props} />
);

function Control(props) {
  return (
    <TextField
      fullWidth
      InputProps={{
        inputComponent,
        inputProps: {
          children: props.children,
          ...props.innerProps
        }
      }}
      {...props.selectProps.textFieldProps}
    />
  );
}

function Option(props) {
  return (
    <MenuItem
      ref={props.innerRef}
      selected={props.isFocused}
      component="div"
      style={{
        fontWeight: props.isSelected ? 500 : 400
      }}
      {...props.innerProps}
    >
      {props.data.disabled ? (
        <BlockRounded style={{ color: '#000', marginRight: 8 }} />
      ) : (
        <Person
          style={{ color: props?.selectProps?.muiTheme?.palette?.secondary?.main, marginRight: 8 }}
        />
      )}
      {props.children}
    </MenuItem>
  );
}

function Placeholder(props) {
  return (
    <PlaceholderTypography color="textSecondary" {...props.innerProps}>
      {props.children}
    </PlaceholderTypography>
  );
}

function SingleValue(props) {
  return (
    <SingleValueTypography {...props.innerProps}>
      {props.children}
    </SingleValueTypography>
  );
}

function ValueContainer(props) {
  return (
    <ValueContainerDiv>
      {props.children}
    </ValueContainerDiv>
  );
}

function MultiValue(props) {
  return (
    <StyledChip
      tabIndex={-1}
      label={props.children}
      isFocused={props.isFocused}
      onDelete={props.removeProps.onClick}
      deleteIcon={<CancelIcon {...props.removeProps} />}
      avatar={
        <Avatar
          style={{
            backgroundColor: props.data.disabled ? '#E0E0E0' : '#667',
            width: '32px',
            height: '32px',
            border: '1px solid #667',
            marginLeft: 0
          }}
        >
          {props.data.disabled ? (
            <BlockRounded style={{ color: '#667' }} />
          ) : (
            <Person style={{ color: '#FFF' }} />
          )}
        </Avatar>
      }
    />
  );
}

const LoadingMessage = props => (
  <MessageTypography color="textSecondary" {...props.innerProps}>
    {props.selectProps.inputValue.length === 1 ? 'Enter 2 chars for searching' : 'Loading...'}
  </MessageTypography>
);

const components = {
  Control,
  MultiValue,
  NoOptionsMessage,
  Option,
  Placeholder,
  SingleValue,
  ValueContainer,
  LoadingMessage
};

const IntegrationReactSelect = props => {
  const {
    label,
    data,
    placeholder,
    defaultData,
    meta = {},
    forceError,
    fetchUsers,
    clearUsers,
    textFieldOnFocus,
    loading,
    setData
  } = props;

  const theme = useTheme();
  const [multi, setMulti] = useState(null);

  const handleChange = value => {
    const newValue = value || [];
    setMulti(newValue);
    setData(newValue);
    if (clearUsers) {
      clearUsers();
    }
  };

  const selectStyles = useMemo(() => buildInlineSelectStyles({ theme }), [theme]);

  const invalid = (!meta.valid && meta.submitFailed) || (meta.error && forceError);

  return (
    <RootContainer className={invalid ? 'invalid' : ''}>
      <NoSsr>
        <Select
          styles={selectStyles}
          textFieldProps={{
            label,
            onChange: fetchUsers || null,
            onBlur: clearUsers || null,
            onFocus: textFieldOnFocus || null,
            InputLabelProps: {
              shrink: true
            }
          }}
          placeholder={placeholder}
          options={loading ? [] : data}
          isLoading={loading}
          components={components}
          value={multi || defaultData}
          onChange={handleChange}
          filterOption={(option, inputValue) => {
            const userData = option.data;
            const isUserName = userData.label.toLowerCase().includes(inputValue.toLowerCase());
            const isUserEmail = userData.email.toLowerCase().includes(inputValue.toLowerCase());
            return isUserName || isUserEmail;
          }}
          test={2}
          isMulti
          menuPlacement="bottom"
          muiTheme={theme}
        />
        {invalid &&
          <ErrorContainer>
            {meta.error}
          </ErrorContainer>}
      </NoSsr>
    </RootContainer>
  );
};

IntegrationReactSelect.propTypes = propTypes;

export default IntegrationReactSelect;
